import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { customerApi } from "@/lib/api";
import { customerStatus } from "@/lib/requestStatus";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function fmt(d) {
  try { return new Date(d).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; }
}

export default function CustomerAccount() {
  const { customer, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customer === false) {
      navigate("/anmelden", { replace: true });
      return;
    }
    if (customer) {
      customerApi.get("/customer/order-requests").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
    }
  }, [customer, navigate]);

  if (!customer) return <div className="on-dark violet-canvas flex-1 w-full py-32 text-center text-[#D8B9EA]">Laden…</div>;

  return (
    <div className="on-dark violet-canvas flex-1 w-full">
      <SEO title="Mein Konto" />
      <div className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#F4C542] mb-2">Mein Konto</div>
            <h1 className="font-serif text-4xl font-light text-[#FFF9F2]" data-testid="account-name">Hallo {customer.name}</h1>
            <p className="text-[#D8B9EA] text-sm mt-1">{customer.email}</p>
          </div>
          <Button onClick={logout} data-testid="customer-logout" variant="outline" className="rounded-md border-[#F4C542]/50 text-[#F4C542] bg-transparent hover:bg-[#F4C542] hover:text-[#32104F]">
            Abmelden
          </Button>
        </div>

        <h2 className="font-serif text-2xl text-[#FFF9F2] mb-4">Meine Anfragen</h2>
        {loading ? (
          <p className="text-[#D8B9EA]">Laden…</p>
        ) : items.length === 0 ? (
          <div className="bg-[#FFF9F2] rounded-md p-6 text-center">
            <p className="text-[#6E5A82]">Du hast noch keine Anfragen gesendet.</p>
            <Link to="/galerie" className="mt-3 inline-block text-[#632A8E] underline">Zur Galerie</Link>
          </div>
        ) : (
          <div className="space-y-4" data-testid="account-requests">
            {items.map((r) => (
              <div key={r.id} className="bg-[#FFF9F2] rounded-md p-5" data-testid={`account-request-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="rounded-md bg-[#632A8E]">{r.kind === "bestellung" ? "Bestellanfrage" : "Anfrage"}</Badge>
                  <span className="font-medium text-[#32104F]">{r.artwork_title}</span>
                  <span className="text-xs text-[#9A86B4] ml-auto">{fmt(r.created_at)}</span>
                </div>
                {r.message && <p className="mt-2 text-[#3A1E55] whitespace-pre-wrap text-sm">{r.message}</p>}
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-[#9A86B4]">Status:</span>
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${customerStatus(r.status).cls}`} data-testid={`account-status-${r.id}`}>
                    {customerStatus(r.status).label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
